<template>
    <v-container fluid>
        <v-card class="pa-4" elevation="2">
            <v-row class="mb-4">
                <v-col>
                    <h2>Info Clientes</h2>
                </v-col>
                <v-col cols="12" sm="6" class="text-right">
                    <v-btn @click="$router.back()">Volver</v-btn>
                </v-col>
            </v-row>

            <v-data-table
                :items="clients"
                :headers="headers"
                class="elevation-1"
                show-expand
                expand-on-click
                v-model:expanded="expanded"
                @update:expanded="onExpandedChange"
                no-data-text="No hi ha clients disponibles"
            >
                <!-- Fila expandida: magatzems del client -->
                <template #expanded-row="{ columns, item }">
                    <td :colspan="columns.length" class="py-2">
                        <div class="nested-block">
                            <div class="nested-title">
                                Magatzems del client: <strong>{{ item.name }}</strong>
                            </div>

                            <v-progress-linear
                                v-if="loadingWarehouses[item.id]"
                                indeterminate class="mb-2"
                            />

                            <v-alert v-if="errorsWarehouses[item.id]" type="error" class="mb-2">
                                {{ errorsWarehouses[item.id] }}
                            </v-alert>

                            <v-data-table
                                :headers="warehouseHeaders"
                                :items="warehousesByClient[item.id] || []"
                                density="compact"
                                class="elevation-0"
                                no-data-text="Sense magatzems"
                            >
                                <template #item.active="{ item }">
                                    <v-icon :color="item.active ? 'green' : 'red'">
                                        {{ item.active ? 'mdi-check' : 'mdi-close' }}
                                    </v-icon>
                                </template>
                            </v-data-table>
                        </div>
                    </td>
                </template>
            </v-data-table>
        </v-card>
    </v-container>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

const router = useRouter()

// Dades base
const clients = ref([])
const expanded = ref([]) // ids de client expandits

// Cache / estat de magatzems per client
const warehousesByClient = reactive({})   // { [clientId]: Warehouse[] }
const loadingWarehouses = reactive({})    // { [clientId]: boolean }
const errorsWarehouses = reactive({})     // { [clientId]: string|null }

// Capçaleres (sense columna d'accions)
const headers = [
    { title: 'ID Odoo', value: 'odoo_id', sortable: true },
    { title: 'Nom', value: 'name', sortable: true },
]

const warehouseHeaders = [
    { title: 'Magatzem', value: 'name', sortable: true },
    { title: 'Adreça', value: 'address', sortable: true },
    { title: 'ID Odoo', value: 'id_odoo', sortable: true },
    { title: 'Actiu', value: 'active', sortable: true },
]

// Carregar clients
const fetchClients = async () => {
    try {
        const { data } = await axios.get('/api/admin/clients_info', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        clients.value = Array.isArray(data) ? data : []
    } catch (e) {
        console.error('Error carregant clients:', e)
        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }
        clients.value = []
    }
}

// Carregar magatzems d’un client (lazy)
const fetchWarehousesForClient = async (clientId) => {
    if (warehousesByClient[clientId]) return
    loadingWarehouses[clientId] = true
    errorsWarehouses[clientId] = null
    try {
        // Endpoint que ha de retornar magatzems RELACIONATS amb el client
        const { data } = await axios.get(`/api/admin/clients/${clientId}/warehouses`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        warehousesByClient[clientId] = Array.isArray(data) ? data : []
    } catch (e) {
        console.error('Error carregant magatzems:', e)
        errorsWarehouses[clientId] = 'No s’han pogut carregar els magatzems.'
        warehousesByClient[clientId] = []
    } finally {
        loadingWarehouses[clientId] = false
    }
}

// Quan canvia la llista d’expandides, fem fetch dels nous
const onExpandedChange = async (newExpanded) => {
    // Detecta nous IDs afegits a l’expansió
    const newlyOpened = newExpanded.filter(id => !expanded.value.includes(id))
    expanded.value = newExpanded
    // Carrega per a cada nou client expandit (lazy)
    for (const id of newlyOpened) {
        await fetchWarehousesForClient(id)
    }
}

onMounted(fetchClients)
</script>

<style scoped>
.nested-block { border-left: 3px solid #eee; padding-left: 12px; margin-left: 8px; }
.nested-title { font-weight: 600; margin-bottom: 8px; }
</style>
